import { z } from "zod";

// ── Input Schema ─────────────────────────────────────────────────────────────

export const EcommercePipelineInputSchema = z.object({
  connectionMethod: z
    .enum(["shopify", "manual"])
    .default("shopify")
    .describe("Method of store connection"),
  shopDomain: z.string().optional().describe("Shopify store domain (e.g. mystore.myshopify.com)"),
  manualDataFileId: z
    .string()
    .optional()
    .describe("Uploaded CSV/PDF file ID containing manual data"),
  businessName: z.string().min(1).describe("Business/brand name"),
  industry: z.string().optional().describe("Industry or vertical"),
  targetAudience: z.string().optional().describe("Primary target audience"),
  averageOrderValue: z.number().optional().describe("Known AOV if available"),
  monthlyRevenue: z.number().optional().describe("Estimated monthly revenue"),
  totalAdSpend: z.number().optional().describe("Total ad spend (for CAC calculation)"),
  totalCustomers: z.number().optional().describe("Total customers (for CAC and LTV)"),
});
export type EcommercePipelineInput = z.infer<typeof EcommercePipelineInputSchema>;

// ── Brand Profile Schema ────────────────────────────────────────────────────

export const BrandProfileSchema = z.object({
  brandVoice: z.string().describe("Brand voice/tone description"),
  targetDemographic: z.string().describe("Primary target demographic"),
  uniqueSellingPoints: z.array(z.string()).describe("Key USPs"),
  competitivePosition: z.string().describe("Market positioning summary"),
  pricePositioning: z.enum(["budget", "mid-range", "premium", "luxury"]).describe("Price tier"),
});
export type BrandProfile = z.infer<typeof BrandProfileSchema>;

// ── Product Category Schema ─────────────────────────────────────────────────

export const ProductCategorySchema = z.object({
  productId: z.string(),
  category: z.string().describe("Google Product Taxonomy category"),
  subcategory: z.string().optional(),
  adAngle: z.string().describe("Recommended advertising angle"),
  targetKeywords: z.array(z.string()).describe("Suggested target keywords"),
});
export type ProductCategory = z.infer<typeof ProductCategorySchema>;

// ── Priority Score Schema ───────────────────────────────────────────────────
// Weights: gross_margin 25%, cac_efficiency 25%, lead_time 20%, market_opportunity 15%, stock_readiness 15%

export const PriorityScoreSchema = z.object({
  productId: z.string(),
  overallScore: z.number().min(0).max(100).describe("Overall ad-readiness score"),
  dimensions: z.object({
    gross_margin: z.number().min(0).max(100).describe("Gross margin health (25%)"),
    cac_efficiency: z.number().min(0).max(100).describe("CAC efficiency for paid ads (25%)"),
    lead_time: z.number().min(0).max(100).describe("Supply chain / lead time readiness (20%)"),
    market_opportunity: z.number().min(0).max(100).describe("Market demand and opportunity (15%)"),
    stock_readiness: z
      .number()
      .min(0)
      .max(100)
      .describe("Inventory readiness to sustain a campaign (15%)"),
  }),
  recommendation: z.enum(["launch", "bundle_launch", "fix_pricing_first", "restock_first", "hold"]),
  reasoning: z.string().describe("Why this score was assigned"),
});
export type PriorityScore = z.infer<typeof PriorityScoreSchema>;

// ── Pipeline Steps ──────────────────────────────────────────────────────────

export const ECOMMERCE_PIPELINE_STEPS = [
  { label: "Store", icon: "store", service: "Shopify OAuth" },
  { label: "Products", icon: "package", service: "Shopify + Gemini" },
  { label: "CAC Estimates", icon: "calculator", service: "Ad Platforms" },
  { label: "Plan", icon: "layout", service: "Dashboard" },
  { label: "Content Inspiration", icon: "sparkles", service: "Meta + TikTok + Gemini" },
  { label: "Ads", icon: "megaphone", service: "OAuth + Ads API" },
];
